<template>
  <div>
    <v-layout wrap>
      <v-flex xs12 mt-6>
        <v-text-field v-model="query" label="Chercher une entreprise par : Nom, id ou Nom de domaine." dense outlined prepend-inner-icon="mdi-magnify"/>
        <div class="text-end">
          <small>{{ companiesToDisplay.length }} résultats.</small>
        </div>
      </v-flex>
      <v-flex xs12 v-for="(company, key) in companiesToDisplay" :key="key">
        <v-card class="mb-8 py-4 px-4">
          <v-layout wrap>
            <v-flex xs8>
              <small>
                <div>
                  <b>{{ company.name }}</b> <small>(id: {{ company.id }})</small>
                  <v-chip v-if="company.isActive === false" x-small color="red" dark class="ml-2">Inactive</v-chip>
                </div>
                <div v-if="company.pricingPlan"><b>Abonnement : {{ company.pricingPlan }}</b> <small>(dossiers_prix)</small></div>
                <div v-if="company.parentCompanyId"><b>Équipe d'un groupe.</b> <small>(Group id : {{ company.parentCompanyId }})</small></div>

                <div class="mt-3" :class="{ 'orange--text': company.dossiersLeftNb === 0 }">{{ company.dossiersLeftNb }} dossiers restants.</div>
                <div>{{ company.usersNb }} utilisateurs.</div>
                <div v-if="!company.hasOrHadSubscription">En période d'essai.</div>
                <div v-else>
                  <div>Dossiers renouvelés le {{ (new Date(company.currentMonthlyPeriodDates.endDate * 1000)).toLocaleDateString() }}.</div>
                </div>

                <div class="mt-3">
                  <div v-if="company.emailDomains">
                    <div>
                      Noms de domaines :
                      <span v-if="company.emailDomains.length > 0"> {{ company.emailDomains.join(', ') }}</span>
                      <span v-else>Aucun.</span>
                    </div>
                    <div>
                      <span @click="showUpdateCompanyEmailDomainsDialog(company)" class="textAsLink">Modifier les noms de domaines</span>
                    </div>
                  </div>
                </div>
                
                <div v-if="company.templates" class="mt-3">
                  <div>
                    Templates : {{ company.templates.length > 0 ? company.templates : 'Aucun.'}}
                  </div>
                  <div>
                    <span @click="showUpdateCompanyTemplatesDialog(company)" class="textAsLink">Modifier les templates</span>
                  </div>
                </div>

                <div class="mt-3">
                  <span v-if="company.hasNoVAT">Pas de TVA (0%)</span>
                  <span v-else>TVA Française (20%)</span>
                  <v-icon small @click="showUpdateCompanyHasNoVAT(company)" class="mb-1 ml-1">mdi-pen</v-icon>
                </div>

                <div class="mt-3">
                  <div>
                    Pricings disponibles : {{  company.onlyDisplayedPlanIds ? company.onlyDisplayedPlanIds.join(', ') : 'Aucun'}}
                    <span v-if="company.onlyDisplayedPlanIds" class="ml-1">(nbDeDossiers_prixMensuel)</span>
                    <div v-if="!company.hasOrHadSubscription" @click="showUpdateCompanyOnlyDisplayedPlanIdsDialog(company.id)" class="textAsLink">
                      Modifier les pricings disponibles
                    </div>
                  </div>
                </div>
              </small>
            </v-flex>
            <v-flex xs4 text-end pt-12>
              <div v-if="!company.hasOrHadSubscription && !company.parentCompanyId">
                <v-btn class="primary" small @click="showAddFreeTrialDossiersNbDialog(company.id)">
                  Ajouter des crédits gratuits
                </v-btn>
              </div>
              <div class="mt-3">
                <v-btn v-if="company.isActive !== false" small outlined color="red" @click="showToggleCompanyIsActiveDialog(company, false)">
                  Désactiver
                </v-btn>
                <v-btn v-else small outlined color="green" @click="showToggleCompanyIsActiveDialog(company, true)">
                  Réactiver
                </v-btn>
              </div>
            </v-flex>
          </v-layout>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="isShowingToggleCompanyIsActiveDialog" width="50%" :persistent="isToggleCompanyIsActivePending">
      <v-card class="pa-6">
        <div v-if="isToggleCompanyIsActivePending">
          <div><b>{{ companyForToggleIsActive ? (newIsActiveValue ? 'Réactivation' : 'Désactivation') : '' }} en cours...</b></div>
          <div v-if="companyForToggleIsActive && !newIsActiveValue" class="mt-1"><small>Tous les utilisateurs de cette entreprise sont en train d'être désactivés...</small></div>
          <v-progress-linear indeterminate color="primary" class="mt-3"></v-progress-linear>
        </div>
        <div v-else-if="companyForToggleIsActive">
          <h3 v-if="!newIsActiveValue">⚠️ Désactiver {{ companyForToggleIsActive.name }} ?</h3>
          <h3 v-else>Réactiver {{ companyForToggleIsActive.name }} ?</h3>
          <div class="mt-3">
            <div v-if="!newIsActiveValue">
              <div><b>Conséquences :</b></div>
              <div>• Tous les utilisateurs ({{ companyForToggleIsActive.usersNb }}) seront <b>désactivés</b> et ne pourront plus se connecter.</div>
              <div>• Aucun nouvel utilisateur ne pourra rejoindre l'entreprise.</div>
              <div>• Les utilisateurs connectés seront déconnectés.</div>
            </div>
            <div v-else>
              <div><b>Conséquences :</b></div>
              <div>• Tous les utilisateurs seront <b>réactivés</b> et pourront se reconnecter.</div>
              <div>• Les utilisateurs individuellement désactivés resteront désactivés.</div>
              <div>• De nouveaux utilisateurs pourront rejoindre l'entreprise.</div>
            </div>
          </div>
          <div v-if="toggleCompanyIsActiveErrorMsg" class="red--text mt-2">{{ toggleCompanyIsActiveErrorMsg }}</div>
          <v-layout wrap mt-4>
            <v-flex xs6 text-center>
              <v-btn small :class="newIsActiveValue ? 'primary' : 'error'" @click="toggleCompanyIsActive">{{ newIsActiveValue ? 'Réactiver' : 'Désactiver' }}</v-btn>
            </v-flex>
            <v-flex xs6 text-center>
              <v-btn small outlined @click="isShowingToggleCompanyIsActiveDialog = false">Annuler</v-btn>
            </v-flex>
          </v-layout>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isShowingUpdateCompanyEmailDomainsDialog" width="50%" :persistent="isUpdateCompanyEmailDomainsPending">
      <v-card class="pa-6">
        <div v-if="isUpdateCompanyEmailDomainsPending">
          <v-progress-linear indeterminate color="primary" class="mt-3"></v-progress-linear>
        </div>
        <div v-else-if="currentCompanyToUpdateEmailDomains">
          <h3>{{ currentCompanyToUpdateEmailDomains.name }}</h3>
          <small>
            <div class="my-3">
              Noms de domaines avant :
              <span v-if="currentCompanyToUpdateEmailDomains.emailDomains.length > 0"> {{ currentCompanyToUpdateEmailDomains.emailDomains.join(', ') }}</span>
              <span v-else>Aucun.</span>
            </div>
            <v-text-field v-model="newCompanyEmailDomainsStr" label="Nouveaux noms de domaines" outlined hide-details />
            <div class="mt-3">
              <div>Lister les noms de domaines les uns après les autres (sans "@"), séparés par des " / "</div>
              <div>Exemple: esnwebservices.com / cvwebservices.com / google.com</div>
            </div>
            <div class="mt-3">
              <div v-if="getNewCompanyEmailDomainsFromStr()">
                <b>
                  Noms de domaines après :
                  <span v-if="getNewCompanyEmailDomainsFromStr().length > 0"> {{ getNewCompanyEmailDomainsFromStr() }}</span>
                  <span v-else>Aucun.</span>
                </b>
              </div>
              <div v-else class="orange--text">
                Mauvais format.
              </div>
              <div>
                <v-layout wrap>
                  <v-flex xs12 text-center mt-4>
                    <v-btn @click="updateCompanyEmailDomains" :disabled="!getNewCompanyEmailDomainsFromStr()" class="primary">Mettre à jour</v-btn>
                  </v-flex>
                </v-layout>
              </div>
            </div>
          </small>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isShowingUpdateCompanyTemplatesDialog" width="50%" :persistent="true">
      <v-card class="pa-6">
        <div v-if="isUpdateCompanyTemplatesPending">
          <v-progress-linear indeterminate color="primary" class="mt-3"></v-progress-linear>
        </div>
        <div v-else-if="currentCompanyToUpdateTemplates">
          <h3>{{ currentCompanyToUpdateTemplates.name }}</h3>
          <small>
            <div class="mt-3">
              <b class="mr-1">Templates avant :</b>
              <span v-if="currentCompanyToUpdateTemplates.templates.length > 0"> {{ currentCompanyToUpdateTemplates.templates }}</span>
              <span v-else>Aucun.</span>
            </div>
            <div class="mb-3">
              <b class="mr-1">Templates après :</b>
              <span v-if="newCompanyTemplates.length > 0">{{ newCompanyTemplates }}</span>
              <span v-else>Aucun.</span>
            </div>

            <v-layout row wrap mt-2>
              <v-flex xs12 v-for="(template, index) in newCompanyTemplates" :key="index">
                <v-card class="px-6 pt-3 mb-6" style="border: 1px solid black">
                  <v-layout row wrap>
                    <v-flex xs6 pl-3>
                      <h2 class="mb-2">Template {{ index + 1 }}</h2>
                    </v-flex>
                    <v-flex xs6 text-end pr-3>
                      <v-btn small outlined @click="deleteATemplateFromNewCompanyTemplates(index)">Supprimer ce template</v-btn>
                    </v-flex>
                  </v-layout>
                  <v-text-field v-model="template.id" label="id (En minuscule sans espace.)" outlined dense/>
                  <v-text-field v-model="template.language" label="language (Deux lettres, en majuscules.)" placeholder="ex: FR ou EN ou DE..." outlined dense />
                  <v-text-field v-model="template.name" label="name" placeholder="ex: Mantu - FR" outlined dense />
                </v-card>
              </v-flex>
              <v-flex xs12 pl-2 mt-6>
                <v-btn @click="addATemplateToNewCompanyTemplates" small class="primary white--text">
                  <v-icon small class="mr-1">mdi-plus</v-icon>
                  <span>Template supplémentaire</span>
                </v-btn>
              </v-flex>
            </v-layout>
        
            <v-layout wrap mt-8>
              <v-flex xs6 text-end pr-10>
                <v-btn @click="updateCompanyTemplates" :disabled="isUpdateCompanyTemplatesDisabled" class="secondary">Mettre à jour</v-btn>
              </v-flex>
              <v-flex xs6 pl-10>
                <v-btn @click="isShowingUpdateCompanyTemplatesDialog = false" outlined>Annuler</v-btn>
              </v-flex>
            </v-layout>
          </small>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isShowingUpdateCompanyHasNoVATDialog" width="50%" :persistent="isUpdateCompanyHasNoVATPending">
      <v-card class="pa-6" v-if="currentCompanyToUpdateHasNoVAT">
        <div v-if="isUpdateCompanyHasNoVATPending">
          <v-progress-linear indeterminate color="primary" class="mt-3"></v-progress-linear>
        </div>
        <div v-else>
          <v-layout wrap>
            <v-flex xs12 text-center>
              <h3>Changement de la TVA</h3>
            </v-flex>
          </v-layout>
          <div class="mb-3 mt-3">
            <div><b>Entreprise:</b> {{ currentCompanyToUpdateHasNoVAT.name }}</div>
            <div>
              <b>Valeur avant changement : </b>
              <span v-if="currentCompanyToUpdateHasNoVAT.hasNoVAT">Pas de TVA (0%)</span>
              <span v-else>TVA Française (20%)</span>
            </div>
          </div>
          <div class="mt-6">
            <v-select v-model="newCompanyHasNoVAT" label="Nouvelle TVA" :items="hasNoVATItems" item-text="text" item-value="value" outlined dense />
          </div>
          <v-layout wrap mt-3>
            <v-flex xs12 text-center>
              <v-btn class="primary mr-4" @click="updateCompanyHasNoVAT" :disabled="newCompanyHasNoVAT == null">Sauvegarder</v-btn>
              <v-btn class="ml-4" outlined @click="isShowingUpdateCompanyHasNoVATDialog = false">Annuler</v-btn>
            </v-flex>
          </v-layout>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isShowingAddFreeTrialDossiersNbDialog" width="50%" :persistent="isAddFreeTrialDossiersNbPending">
      <v-card class="pa-6">
        <div v-if="isAddFreeTrialDossiersNbPending">
          <div><b>Un momento, paquito...</b></div>
          <small>
            <div><b class="orange--text">Attendre 10-15 secondes pour</b> que le nombre de crédits gratuits soit mis à jour en base de données.</div>
            <div><b class="orange--text">Rafraîchir cette page</b> pour voir si le changement a bien été fait.</div>
          </small>
          <v-progress-linear indeterminate color="primary" class="mt-3"></v-progress-linear>
        </div>
        <div v-else-if="companyIdToAddFreeTrialDossiersNb">
          <div class="text-center mb-2">
            <h3>{{ currentCompanyToAddFreeTrialDossiersNb.name }}</h3>
          </div>
          <small>
            <div>Nombre total de crédits offerts : {{ currentCompanyToAddFreeTrialDossiersNb.freeTrialDossiersNb }} </div>
            <div>Nombre de crédits restants : {{ currentCompanyToAddFreeTrialDossiersNb.dossiersLeftNb  }}</div>
          </small>
          <div class="mt-4 mb-3">
            <v-text-field dense hide-details type="number" outlined v-model.number="additionalFreeTrialDossiersNb" label="Nombre de crédits à offrir en plus" :step="1" />
          </div>
          <small>
            <div>Nouveau nombre total de crédits offerts : {{ Math.max(0, currentCompanyToAddFreeTrialDossiersNb.freeTrialDossiersNb + additionalFreeTrialDossiersNb) }} </div>
            <div><b>Nouveau nombre de crédits restants : {{ Math.max(0, currentCompanyToAddFreeTrialDossiersNb.dossiersLeftNb + additionalFreeTrialDossiersNb) }}</b></div>
          </small>
          <div class="mt-8 text-center">
            <v-btn @click="addFreeTrialDossiersNb" class="secondary" :disabled="!additionalFreeTrialDossiersNb || !Number.isInteger(additionalFreeTrialDossiersNb)">Mettre à jour</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isShowingUpdateCompanyOnlyDisplayedPlanIdsDialog" width="50%" :persistent="isUpdateCompanyOnlyDisplayedPlanIdsPending">
      <v-card class="pa-6">
        <div v-if="isUpdateCompanyOnlyDisplayedPlanIdsPending">
          <v-progress-linear indeterminate color="primary" class="mt-3"></v-progress-linear>
        </div>
        <div v-else-if="companyIdToUpdateOnlyDisplayedPlanIds">
          <div class="text-center mb-2">
            <h3>{{ currentCompanyToUpdateOnlyDisplayedPlanIds.name }}</h3>
          </div>
          <small>
            <div>Pricings actuellement disponibles à l'entreprise : {{  currentCompanyToUpdateOnlyDisplayedPlanIds.onlyDisplayedPlanIds ? currentCompanyToUpdateOnlyDisplayedPlanIds.onlyDisplayedPlanIds.join(', ') : 'Aucun' }} </div>
          </small>
          <div class="mt-4 mb-3">
            <div>
              <v-select
                v-model="modeOfNewOnlyDisplayedPlanIds"
                label="Nouveaux pricings"
                :items="allModesOfNewOnlyDisplayedPlanIds"
                item-text="name"
                item-value="id"
                outlined
                dense
              ></v-select>
            </div>
            <div v-if="modeOfNewOnlyDisplayedPlanIds === 'custom'">
              <v-autocomplete
                v-model="newOnlyDisplayedPlanIdsModel"
                @change="setNewOnlyDisplayedPlanIdsFromItsModel"
                :items="allPlanItems"
                item-text="name"
                item-value="id"
                label="Pricings"
                multiple
                outlined
                dense
                hide-details
                small-chips
                deletable-chips
              />
            </div>
          </div>
          <small v-if="newOnlyDisplayedPlanIds !== undefined && (newOnlyDisplayedPlanIds === null || newOnlyDisplayedPlanIds.length > 0)">
            <div>Nouvelle liste de pricings disponibles à l'entreprise :</div>
            <div v-if="newOnlyDisplayedPlanIds === null">Aucun</div>
            <div v-else>
              {{ newOnlyDisplayedPlanIds.join(', ') }}
            </div>
          </small>
          <div v-if="newOnlyDisplayedPlanIdsHasMultiplePricesForSameDossiersNb" class="orange--text">
            Des abonnements sélectionnés ont le même nombre de dossiers mais des prix différents.
          </div>
          <div class="mt-8 text-center">
            <v-btn @click="updateCompanyOnlyDisplayedPlanIds" class="secondary" :disabled="newOnlyDisplayedPlanIds === undefined || !(newOnlyDisplayedPlanIds === null || newOnlyDisplayedPlanIds.length > 0) || newOnlyDisplayedPlanIdsHasMultiplePricesForSameDossiersNb">Mettre à jour</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>  
</template>

<script>

import { getStripePlans } from '@/helpers/subscriptions.js';

export default {
  props: {
    allCompanies: Array
  },
  data: () => ({
    query: "",

    companyIdToAddFreeTrialDossiersNb: null,
    isShowingAddFreeTrialDossiersNbDialog: false,
    isAddFreeTrialDossiersNbPending: false,
    additionalFreeTrialDossiersNb: 0,

    companyIdToUpdateOnlyDisplayedPlanIds: null,
    isShowingUpdateCompanyOnlyDisplayedPlanIdsDialog: false,
    isUpdateCompanyOnlyDisplayedPlanIdsPending: false,
    newOnlyDisplayedPlanIds: undefined,

    companyIdToUpdateHasNoVAT: null,
    newCompanyHasNoVAT: null,
    isShowingUpdateCompanyHasNoVATDialog: false,
    isUpdateCompanyHasNoVATPending: false,
    hasNoVATItems: [
      { text: 'TVA Française (20%)', value: false },
      { text: 'Pas de TVA (0%)', value: true },
    ],

    companyIdToUpdateEmailDomains: null,
    isShowingUpdateCompanyEmailDomainsDialog: false,
    newCompanyEmailDomainsStr: '',
    isUpdateCompanyEmailDomainsPending: false,

    companyIdToUpdateTemplates: null,
    isShowingUpdateCompanyTemplatesDialog: false,
    isUpdateCompanyTemplatesPending: false,
    newCompanyTemplates: [],

    modeOfNewOnlyDisplayedPlanIds: null,
    allModesOfNewOnlyDisplayedPlanIds: [
      { id: 'none', name: 'Aucun' },
      { id: "list_3e", name: 'Échelle à 3€, dégressive' },
      { id: "list_2e", name: 'Échelle à 2€, non dégressive' },
      { id: "50_159_&_100_299", name: '50_159 & 100_299' },
      { id: "list_big_yearly_discount", name: 'Échelle avec gros discount annuel' },
      { id: "custom", name: 'Custom' },
    ],

    plans: null,

    companyForToggleIsActive: null,
    newIsActiveValue: null,
    isShowingToggleCompanyIsActiveDialog: false,
    isToggleCompanyIsActivePending: false,
    toggleCompanyIsActiveErrorMsg: null
  }),
  async mounted() {
    this.plans = await getStripePlans(this.$db);
    if (this.$route.query.companyQuery) this.query = this.$route.query.companyQuery
  },
  computed: {
    companiesToDisplay() {
      return this.allCompanies
        .filter(item => {
          return [item.id, item.name, (item.emailDomains || []).join(' ')].join(' ').toLocaleLowerCase().indexOf(this.query.toLocaleLowerCase()) > -1
        })
        .sort((a, b) => {
          if (b.hasOrHadSubscription !== a.hasOrHadSubscription) {
            return b.hasOrHadSubscription ? 1 : -1 
          } else {
            return b.dossiersLeftNb > a.dossiersLeftNb ? -1 : 1
          }
        })
    },
    currentCompanyToAddFreeTrialDossiersNb() {
      return this.allCompanies.find(company => company.id === this.companyIdToAddFreeTrialDossiersNb)
    },
    currentCompanyToUpdateOnlyDisplayedPlanIds() {
      return this.allCompanies.find(company => company.id === this.companyIdToUpdateOnlyDisplayedPlanIds)
    },
    currentCompanyToUpdateEmailDomains() {
      return this.allCompanies.find(company => company.id === this.companyIdToUpdateEmailDomains)
    },
    currentCompanyToUpdateTemplates() {
      return this.allCompanies.find(company => company.id === this.companyIdToUpdateTemplates)
    },
    currentCompanyToUpdateHasNoVAT() {
      return this.allCompanies.find(company => company.id === this.companyIdToUpdateHasNoVAT)
    },
    allPlanItems() {
      const plans = this.plans
      return plans.monthlyPlans.concat(plans.hiddenMonthlyPlans).concat(plans.yearlyPlans).concat(plans.hiddenYearlyPlans)
        .filter(plan => plan.id !== 'standard')
        .sort((a, b) => {
          if (a.numberOfDossiersPerMonth === b.numberOfDossiersPerMonth) {
            const aAmount = a.isYearly ? a.amountBeforeYearlyDiscount/12 : a.amount
            const bAmount = b.isYearly ? b.amountBeforeYearlyDiscount/12 : b.amount
            return aAmount - bAmount
          } else {
            return a.numberOfDossiersPerMonth - b.numberOfDossiersPerMonth
          }
        })
        .map(plan => {
          return { id: plan.id, name: plan.id }
        })
    },
    allYearlyPlanIds() {
      const plans = this.plans
      return plans.yearlyPlans.concat(plans.hiddenYearlyPlans)
        .filter(plan => plan.id !== 'standardYearly')
        .map(plan =>  plan.id)
    },
    newOnlyDisplayedPlanIdsHasMultiplePricesForSameDossiersNb() {
      if (!this.newOnlyDisplayedPlanIds) return false
      const selectedMonthlyPlans = this.newOnlyDisplayedPlanIds.map(planId => this.getPlanById(planId)).filter(plan => !plan.isYearly)
      return [... new Set(selectedMonthlyPlans.map(plan => plan.numberOfDossiersPerMonth))].length !== selectedMonthlyPlans.length
    },
    isUpdateCompanyTemplatesDisabled() {
      return Boolean(this.newCompanyTemplates.find(template => {
        if (!template.id || !template.language || !template.name) return true;
        if (template.id.toLowerCase() !== template.id) return true
        if (template.language.toUpperCase() !== template.language) return true
        return false
      }))
    }
  },
  methods: {
    showAddFreeTrialDossiersNbDialog(companyId) {
      this.companyIdToAddFreeTrialDossiersNb = companyId
      this.isShowingAddFreeTrialDossiersNbDialog = true
    },
    async addFreeTrialDossiersNb() {
      this.isAddFreeTrialDossiersNbPending = true
      await this.$functions.httpsCallable('addFreeTrialDossiersNb')({
        companyId: this.companyIdToAddFreeTrialDossiersNb,
        freeTrialDossiersNb: Math.max(0, this.currentCompanyToAddFreeTrialDossiersNb.freeTrialDossiersNb + this.additionalFreeTrialDossiersNb)
      })
      setTimeout(() => {
        this.companyIdToAddFreeTrialDossiersNb = null
        this.isShowingAddFreeTrialDossiersNbDialog = false
        this.isAddFreeTrialDossiersNbPending = false
      }, 5000)
    },
    showUpdateCompanyHasNoVAT(company) {
      this.companyIdToUpdateHasNoVAT = company.id
      this.newCompanyHasNoVAT = null
      this.isShowingUpdateCompanyHasNoVATDialog = true
    },
    async updateCompanyHasNoVAT() {
      this.isUpdateCompanyHasNoVATPending = true
      await this.$functions.httpsCallable('updateCompanyHasNoVAT')({
        companyId: this.companyIdToUpdateHasNoVAT,
        hasNoVAT: this.newCompanyHasNoVAT
      })
      this.$router.push(`/admin?activeTab=listCompanies&companyQuery=${this.companyIdToUpdateHasNoVAT}`)
      this.$router.go()
    },
    showUpdateCompanyEmailDomainsDialog(company) {
      this.companyIdToUpdateEmailDomains = company.id
      this.newCompanyEmailDomainsStr = company.emailDomains.join(' / ')
      this.isShowingUpdateCompanyEmailDomainsDialog = true
    },
    async updateCompanyEmailDomains() {
      this.isUpdateCompanyEmailDomainsPending = true
      await this.$functions.httpsCallable('updateCompanyEmailDomains')({
        companyId: this.companyIdToUpdateEmailDomains,
        emailDomains: this.getNewCompanyEmailDomainsFromStr()
      })
      this.$router.push(`/admin?activeTab=listCompanies&companyQuery=${this.companyIdToUpdateEmailDomains}`)
      this.$router.go()
      // this.isShowingUpdateCompanyEmailDomainsDialog = false
      // this.isUpdateCompanyEmailDomainsPending = false
      // this.companyIdToUpdateEmailDomains = null
    },
    getNewCompanyEmailDomainsFromStr() {
      let domainNames = this.newCompanyEmailDomainsStr.split('/').map(str => str.trim().toLowerCase()).filter(Boolean)
      if (domainNames.find(domainName => domainName.includes('@') || !domainName.includes('.') || domainName.includes(' ') || domainName.split('.').length < 2 || domainName.split('.').filter(x => !x.trim()).length > 0 )) return null;
      else return domainNames
    },
    async updateCompanyTemplates() {
      this.isUpdateCompanyTemplatesPending = true
      await this.$functions.httpsCallable('updateCompanyTemplates')({
        companyId: this.companyIdToUpdateTemplates,
        templates: this.newCompanyTemplates
      })
      this.$router.push(`/admin?activeTab=listCompanies&companyQuery=${this.companyIdToUpdateTemplates}`)
      this.$router.go()
    },
    showUpdateCompanyTemplatesDialog(company) {
      this.companyIdToUpdateTemplates = company.id
      this.newCompanyTemplates = JSON.parse(JSON.stringify(company.templates))
      this.isShowingUpdateCompanyTemplatesDialog = true
    },
    addATemplateToNewCompanyTemplates() {
      this.newCompanyTemplates.push({
        id: '',
        language: 'FR',
        name: ''
      })
    },
    deleteATemplateFromNewCompanyTemplates(templateIndex) {
      this.newCompanyTemplates.splice(templateIndex, 1)
    },
    showUpdateCompanyOnlyDisplayedPlanIdsDialog(companyId) {
      this.companyIdToUpdateOnlyDisplayedPlanIds = companyId
      this.isShowingUpdateCompanyOnlyDisplayedPlanIdsDialog = true
    },
    async updateCompanyOnlyDisplayedPlanIds() {
      this.isUpdateCompanyOnlyDisplayedPlanIdsPending = true
      await this.$functions.httpsCallable('updateCompanyOnlyDisplayedPlanIds')({
        companyId: this.companyIdToUpdateOnlyDisplayedPlanIds,
        onlyDisplayedPlanIds: this.newOnlyDisplayedPlanIds
      })
      this.$router.push(`/admin?activeTab=listCompanies&companyQuery=${this.companyIdToUpdateOnlyDisplayedPlanIds}`)
      this.$router.go()
      // this.companyIdToUpdateOnlyDisplayedPlanIds = null
      // this.isShowingUpdateCompanyOnlyDisplayedPlanIdsDialog = false
      // this.isUpdateCompanyOnlyDisplayedPlanIdsPending = false
    },
    setNewOnlyDisplayedPlanIdsFromItsModel() {
      this.newOnlyDisplayedPlanIds = this.newOnlyDisplayedPlanIdsModel.sort((a, b) => {
        const planA = this.getPlanById(a)
        const planB = this.getPlanById(b)
        if (planA.numberOfDossiersPerMonth === planB.numberOfDossiersPerMonth) {
          return planA.amount - planB.amount
        } else {
          return planA.numberOfDossiersPerMonth - planB.numberOfDossiersPerMonth
        }
      })
    },
    getPlanById(planId) {
      const plans = this.plans
      const allPlans = plans.monthlyPlans.concat(plans.hiddenMonthlyPlans).concat(plans.yearlyPlans).concat(plans.hiddenYearlyPlans)
      return allPlans.find(plan => plan.id === planId)
    },
    showToggleCompanyIsActiveDialog(company, newIsActiveValue) {
      this.companyForToggleIsActive = company
      this.newIsActiveValue = newIsActiveValue
      this.toggleCompanyIsActiveErrorMsg = null
      this.isShowingToggleCompanyIsActiveDialog = true
    },
    async toggleCompanyIsActive() {
      this.isToggleCompanyIsActivePending = true
      this.toggleCompanyIsActiveErrorMsg = null
      try {
        await this.$functions.httpsCallable('updateCompanyIsActive')({
          companyId: this.companyForToggleIsActive.id,
          isActive: this.newIsActiveValue
        })
        this.$router.push(`/admin?activeTab=listCompanies&companyQuery=${this.companyForToggleIsActive.id}`)
        this.$router.go()
      } catch (err) {
        this.toggleCompanyIsActiveErrorMsg = err.message || 'Erreur lors de la mise à jour.'
        this.isToggleCompanyIsActivePending = false
      }
    }
  },
  watch: {
    isShowingAddFreeTrialDossiersNbDialog: {
      handler(val) {
        if (!val) {
          this.companyIdToAddFreeTrialDossiersNb = null
          this.additionalFreeTrialDossiersNb = 0
        }
      }
    },
    isShowingUpdateCompanyEmailDomainsDialog: {
      handler(val) {
        if (!val) {
          this.companyIdToUpdateEmailDomains = null
          this.newCompanyEmailDomainsStr = ''
        }
      }
    },
    isShowingUpdateCompanyTemplatesDialog: {
      handler(val) {
        if (!val) {
          this.companyIdToUpdateTemplates = null
          this.newCompanyTemplates = []
        }
      }
    },
    isShowingUpdateCompanyHasNoVATDialog: {
      handler(val) {
        if (!val) {
          this.companyIdToUpdateHasNoVAT = null
          this.newCompanyHasNoVAT = null
        }
      }
    },
    isShowingUpdateCompanyOnlyDisplayedPlanIdsDialog: {
      handler(val) {
        if (!val) {
          this.companyIdToUpdateOnlyDisplayedPlanIds = null
          this.newOnlyDisplayedPlanIds = undefined
          this.modeOfNewOnlyDisplayedPlanIds = null
        } else {
          if (this.currentCompanyToUpdateOnlyDisplayedPlanIds.onlyDisplayedPlanIds) {
            this.modeOfNewOnlyDisplayedPlanIds = 'custom'
            this.newOnlyDisplayedPlanIds = this.currentCompanyToUpdateOnlyDisplayedPlanIds.onlyDisplayedPlanIds
          }
        }
      }
    },
    modeOfNewOnlyDisplayedPlanIds: {
      handler(val) {
        if (val === 'none') {
          this.newOnlyDisplayedPlanIds = null
        } else if (val === 'list_big_yearly_discount') {
          this.newOnlyDisplayedPlanIds = ['50_159', '50_116_yearly', '100_299', '100_190_yearly', '150_439', '150_239_yearly', '200_579', '200_263_yearly', '10000_799', '10000_363_yearly']
          this.modeOfNewOnlyDisplayedPlanIds = 'custom'
        } else if (val === 'list_3e') {
          this.newOnlyDisplayedPlanIds = ['50_159', '50_159_yearly', '100_299', '100_299_yearly', '150_439', '150_439_yearly', '200_579', '200_579_yearly', '250_719', '250_719_yearly', '300_859', '300_859_yearly', '350_989', '350_989_yearly', '400_1129', '400_1129_yearly', '450_1269', '450_1269_yearly', '500_1399', '500_1399_yearly', '700_1919', '700_1919_yearly', '1000_2699', '1000_2699_yearly']
          this.modeOfNewOnlyDisplayedPlanIds = 'custom'
        } else if (val === 'list_2e') {
          this.newOnlyDisplayedPlanIds = ['100_199', '100_199_yearly', '150_299', '150_299_yearly', '200_399', '200_399_yearly', '250_499', '250_499_yearly', '300_599', '300_599_yearly', '350_699', '350_699_yearly', '400_799', '400_799_yearly', '450_899', '450_899_yearly', '500_999', '500_999_yearly', '700_1399', '700_1399_yearly', '1000_1999', '1000_1999_yearly']
          this.modeOfNewOnlyDisplayedPlanIds = 'custom'
        } else if (val === '50_159_&_100_299') {
          this.newOnlyDisplayedPlanIds = ['50_159', '50_159_yearly', '100_299', '100_299_yearly']
          this.modeOfNewOnlyDisplayedPlanIds = 'custom'
        } else if (val === 'custom') {
          if (this.newOnlyDisplayedPlanIds) {
            this.newOnlyDisplayedPlanIdsModel = this.newOnlyDisplayedPlanIds
          } else {
            this.newOnlyDisplayedPlanIdsModel = []
          }
        }
      }
    },
    isShowingToggleCompanyIsActiveDialog: {
      handler(val) {
        if (!val) {
          this.companyForToggleIsActive = null
          this.newIsActiveValue = null
          this.toggleCompanyIsActiveErrorMsg = null
        }
      }
    }
  }

}
</script>
